import type { ColDef } from 'ag-grid-community';
import type { FC } from 'react';
import { Spinner } from 'react-bootstrap';
import type { ReservationState } from '../../../modules/enums/types';
import { DataTable } from '../../Tables/DataTable';
import { type ReservationOption, useReservationOptions } from './useReservationOptions';

interface ReservationPickerTableProps {
  active: boolean;
  states: ReservationState[];
  excludeReservationIds?: Set<string>;
  /** Row click → the full option (reservationId + ISO window), or null when cleared. */
  onSelect: (selection: ReservationOption | null) => void;
  emptyLabel?: string;
}

const COLUMNS: ColDef<ReservationOption>[] = [
  { headerName: 'Guest', field: 'guest', flex: 2 },
  { headerName: 'Room', field: 'room', flex: 2 },
  { headerName: 'Arrival', field: 'arrival', flex: 1 },
  { headerName: 'Departure', field: 'departure', flex: 1 },
];

/**
 * Searchable, single-select table of reservations, backed by the shared
 * `useReservationOptions` fetch/filter. Reused by the Promote and Create flows.
 */
export const ReservationPickerTable: FC<ReservationPickerTableProps> = ({
  active,
  states,
  excludeReservationIds,
  onSelect,
  emptyLabel = 'No reservations available.',
}) => {
  const { options, loading } = useReservationOptions({
    active,
    states,
    excludeReservationIds,
  });

  if (loading) {
    return (
      <div className="text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (options.length === 0) {
    return <p className="text-muted m-0">{emptyLabel}</p>;
  }

  return (
    <DataTable
      changeState={0}
      rowData={options}
      columnDefs={COLUMNS}
      pagination
      paginationPageSize={5}
      rowSelection={{
        mode: 'singleRow',
        checkboxes: false,
        enableClickSelection: true,
      }}
      onSelection={(selected: ReservationOption[]) => onSelect(selected[0] ?? null)}
    />
  );
};
